/**
 * Types for the Document Storage feature ("My documents").
 *
 * Mirrors the backend `SavedDocument` dataclass (`gymea_edu/storage/models.py`)
 * and the `/api/documents/storage/...` endpoint shapes (see
 * `gymea_edu/routes/storage.py`).
 *
 * The preview and rename response shapes are declared here even though the v1
 * UI never calls those endpoints — the backend serves them, so keeping the
 * types documents the full API surface.
 */

export type DocumentType =
  | "unit_of_work"
  | "lesson_plan"
  | "worksheet"
  | "slideshow";

export interface SavedDocument {
  document_id: string;
  document_type: DocumentType;
  title: string;
  filename: string;
  size_bytes: number;
  content_type: string;
  s3_key: string;
  created_at: string;
  metadata: Record<string, unknown>;
  // Populated only on the get-single endpoint:
  download_url?: string;
  preview_url?: string;
  office_viewer_url?: string;
  exceeds_viewer_limit?: boolean;
}

export interface ListDocumentsResponse {
  documents: SavedDocument[];
  count: number;
}

export interface DownloadUrlResponse {
  download_url: string;
}

/** Shape of `GET /{id}/preview`. No v1 caller — the preview UI is deferred. */
export interface PreviewUrlResponse {
  preview_url: string;
  office_viewer_url: string;
  exceeds_viewer_limit: boolean;
}

export interface DeleteDocumentResponse {
  deleted: boolean;
}

/** Optional filters for the list endpoint. */
export interface ListDocumentsParams {
  type?: DocumentType;
  search?: string;
}

/** Human-readable labels per document type (used by filter chips + rows). */
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  unit_of_work: "Unit of Work",
  lesson_plan: "Lesson Plan",
  worksheet: "Worksheet",
  slideshow: "Slideshow",
};
